/**
 * Pricing page — 3 plans (Mensile / Annuale / Lifetime).
 *
 * Flow:
 *   1. Unauthenticated → bottoni "Accedi/Registrati" che rimandano a /auth?redirect=/pricing
 *   2. Authenticated   → bottone "Inizia trial / Acquista"
 *                         - chiama GET /api/billing/checkout?plan=<key>
 *                         - apre l'overlay LemonSqueezy (window.LemonSqueezy)
 *   3. Già Premium     → mostra banner di stato + link a /account
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sparkles, Check, Crown, Zap, Loader2, ArrowRight, X, ShieldCheck,
  Target, Flame, Filter, Sword, Calendar, TrendingUp, ChevronDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  bg: "#050507",
  surface: "#0A0A0D",
  surfaceHi: "#0F0F15",
  border: "rgba(255,255,255,0.08)",
  text: "#FFFFFF",
  muted: "#71717A",
  accent: "#C8FF00",
  cyan: "#06B6D4",
  violet: "#A78BFA",
  red: "#EF4444",
};

const PLAN_BENEFITS = [
  "Pronostici AI del Giorno (Claude Opus 5)",
  "Brain Lab · Discovery e Scansione Target",
  "Crossfilter avanzati + Filtro Gundam Score",
  "Distribuzione Gol Avanzata + Heatmap",
  "Monte Carlo · 10.000 simulazioni",
  "Value Bet sharp + Analisi Tattica IA",
  "Samurai Picker illimitato",
  "Multipla del Ronin illimitata",
  "Daymio Trends · Calendario predittivo",
];

const SUBLINE = {
  monthly:  (p) => "Fatturato mensile",
  annual:   (p) => {
    // Compute the effective monthly rate from the LIVE annual price
    // (fetched from Stripe by /api/billing/plans) so the marketing copy
    // never drifts from the real charge — if you tweak the annual price
    // on Stripe, this label updates automatically.
    const eq = p?.price_eur ? (p.price_eur / 12).toFixed(2).replace(".", ",") : "—";
    return `Fatturato annuale · equivalente €${eq}/mese`;
  },
  lifetime: (p) => "Pagamento una tantum · accesso a vita",
};

const PREMIUM_FEATURES = [
  {
    icon: Target,
    accent: "#F6C643",
    eyebrow: "Il prono e la combo più sicuri di ogni partita",
    title: "Target Kitano + Hyper Combo",
    body: "Per ogni singola partita, il Target Kitano ti consegna il pronostico più sicuro — l'esito singolo con la probabilità più alta secondo il nostro algoritmo. L'Hyper Combo invece è la combinazione di due mercati correlati con il miglior compromesso fra affidabilità e quota. Apri la partita, leggi entrambi i suggerimenti, scegli su quale puntare.",
    bullets: ["Il pronostico singolo più sicuro per match", "La combo più sicura per match", "Probabilità calcolata su modello multi-feature"],
  },
  {
    icon: Filter,
    accent: "#9DF6FF",
    eyebrow: "Filtra le partite, scopri le combo vincenti",
    title: "Crossfilter avanzati + Brain Lab",
    body: "Con i Crossfilter puoi filtrare tutte le partite del giorno selezionando fino a tre filtri in combinazione (es. \"Over 2.5 ≥70%\" + \"Gundam ≥60\" + \"Affidabilità Alta\") e isolare solo i match che ti interessano. Brain Lab fa il passo successivo: lavora in backtest su mesi di dati reali e ti mostra quali combinazioni di filtri sono storicamente più vincenti, con hit-rate e ROI verificabili.",
    bullets: ["Filtri combinabili fino a 3 condizioni", "Discovery automatica delle combo vincenti", "Backtest reale con hit-rate e ROI per ogni preset"],
  },
  {
    icon: Sword,
    accent: "#FF6FD8",
    eyebrow: "Quattro pronostici, un colpo solo",
    title: "Samurai Picker illimitato",
    body: "Scegli la partita, il Samurai forgia quattro pronostici differenti — Shinobi (sicuro), Samurai (equilibrato), Gundam (high-value), Shogun (premium). Niente limite giornaliero come per i Free: scansiona tutte le partite del giorno e crea la tua sequenza vincente. Disponibile anche la Multipla del Ronin: tre samurai in schedina, quote alte ma logica intatta.",
    bullets: ["4 pronostici per partita, 4 livelli di rischio", "Multipla del Ronin illimitata", "Hit-rate storico visibile in tempo reale"],
  },
  {
    icon: Calendar,
    accent: "#22FFA3",
    eyebrow: "Le partite più mature di domani",
    title: "Daymio Trends",
    body: "Il calendario predittivo che mostra le partite con i giocatori \"in ritardo\" statistico: chi sta per segnare, chi è atteso al gol, chi rischia il cartellino. Filtra per minuti dall'ultimo evento, media a 90', delay-rate e trova le quote nascoste che il mercato non ha ancora prezzato.",
    bullets: ["Ritardi statistici per ogni evento (gol, tiri, cartellini, falli)", "Filtri delay × media per identificare maturità", "Calendario navigabile su tutte le leghe whitelist"],
  },
  {
    icon: TrendingUp,
    accent: "#C8FF00",
    eyebrow: "La quota giusta prima di tutti",
    title: "Value Bet del Match",
    body: "Per ogni partita confrontiamo la probabilità del nostro modello con la quota del bookmaker e ti segnaliamo solo i mercati dove c'è un edge sharp positivo. Niente \"consigli\" sparati, solo Value Bet matematicamente sostenute con segnali di supporto (portiere in forma, momentum, xG recente, infortuni chiave).",
    bullets: ["Edge bookmaker calcolato live", "Segnali di supporto contestuali", "Trap-detection sui mercati popolari"],
  },
];

const PLAN_META = {
  monthly:  { icon: Zap,    badge: null, color: "#06B6D4",  bg: "rgba(6,182,212,0.05)", border: "rgba(6,182,212,0.45)", originalPrice: 29.99 },
  annual:   { icon: Sparkles,badge: null,          color: "#C8FF00", bg: "rgba(200,255,0,0.05)", border: "rgba(200,255,0,0.45)" },
  lifetime: { icon: Crown,  badge: "BEST VALUE",   color: "#A78BFA", bg: "rgba(167,139,250,0.05)", border: "rgba(167,139,250,0.45)" },
};

// Compute the discount % dynamically from live price + reference "originalPrice".
// When you update the Stripe price, the badge auto-adjusts (no code change).
const computeDiscountBadge = (planKey, livePriceEur) => {
  const meta = PLAN_META[planKey];
  if (!meta) return meta?.badge || null;
  if (meta.badge) return meta.badge;                          // static badge (e.g. "BEST VALUE")
  if (!meta.originalPrice || livePriceEur == null) return null;
  const pct = Math.round(((meta.originalPrice - livePriceEur) / meta.originalPrice) * 100);
  if (pct < 5) return null;                                    // hide badge if discount is trivial
  return `OFFERTA -${pct}%`;
};

// Ordine display: annual a sinistra, monthly al CENTRO (featured), lifetime a destra.
const PLAN_ORDER = ["annual", "monthly", "lifetime"];

export default function PricingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isPremium, premiumStatus, getToken, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sportium, setSportium] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState(null);
  const [error, setError] = useState(null);
  // Telegram username capture modal (shown before each Stripe checkout)
  const [tgModalPlan, setTgModalPlan] = useState(null);     // planKey or null
  const [tgInput, setTgInput] = useState("");
  const [tgError, setTgError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [plansRes, sportiumRes] = await Promise.all([
          axios.get(`${API}/billing/plans`),
          axios.get(`${API}/billing/sportium`).catch(() => ({ data: { enabled: false } })),
        ]);
        setPlans(plansRes.data.plans || []);
        setSportium(sportiumRes.data?.enabled ? sportiumRes.data : null);
      } catch (e) {
        setError("Impossibile caricare i piani.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Pre-fill the modal with the user's saved telegram if any
  useEffect(() => {
    if (tgModalPlan && user?.telegram_username) {
      setTgInput(user.telegram_username);
    }
  }, [tgModalPlan, user]);

  const handleSubscribe = (planKey) => {
    setError(null);
    if (!isAuthenticated) {
      navigate(`/auth?redirect=/pricing&mode=register`);
      return;
    }
    // Open the Telegram capture modal — actual checkout fires from there.
    setTgError(null);
    setTgInput(user?.telegram_username || "");
    setTgModalPlan(planKey);
  };

  const validateTelegram = (raw) => {
    let v = (raw || "").trim();
    if (!v) return { ok: false, msg: "Inserisci il tuo username Telegram." };
    if (!v.startsWith("@")) v = "@" + v;
    const handle = v.slice(1);
    if (!/^[A-Za-z0-9_]{5,32}$/.test(handle)) {
      return { ok: false, msg: "Formato non valido. 5-32 caratteri (lettere, numeri, _). Esempio: @mario_rossi" };
    }
    return { ok: true, value: v };
  };

  const confirmTelegramAndCheckout = async () => {
    const v = validateTelegram(tgInput);
    if (!v.ok) {
      setTgError(v.msg);
      return;
    }
    const planKey = tgModalPlan;
    setBusyPlan(planKey);
    setTgError(null);
    try {
      const res = await axios.post(
        `${API}/billing/checkout`,
        {
          plan: planKey,
          origin_url: window.location.origin,
          telegram_username: v.value,
        },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const url = res.data?.url;
      if (!url) throw new Error("URL checkout non disponibile");
      // Stripe checkout is hosted on stripe.com — full redirect.
      window.location.href = url;
    } catch (e) {
      setTgError(e?.response?.data?.detail || e.message || "Errore checkout");
    } finally {
      setBusyPlan(null);
    }
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text }} className="grid-bg">
      {/* Header */}
      <header style={{
        padding: "1.4rem 1.5rem", borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 16, flexWrap: "wrap",
      }}>
        <button
          onClick={() => navigate("/")}
          data-testid="pricing-back-home"
          style={{
            background: "transparent", border: `1px solid ${C.border}`,
            color: C.muted, padding: "0.45rem 0.85rem",
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem",
            letterSpacing: "0.16em", textTransform: "uppercase",
            cursor: "pointer", display: "inline-flex", gap: 6, alignItems: "center",
          }}
        >
          <X size={12} /> Torna alla Home
        </button>
        <div style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
          fontSize: "1.5rem", letterSpacing: "0.16em", textTransform: "uppercase",
        }}>
          Bet<span style={{ color: C.accent }}>Cracker</span>
        </div>
        {isAuthenticated && (
          <button
            onClick={() => navigate("/account")}
            data-testid="pricing-go-account"
            style={{
              background: "transparent", border: `1px solid ${C.border}`,
              color: C.text, padding: "0.45rem 0.85rem",
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem",
              letterSpacing: "0.16em", textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Account
          </button>
        )}
      </header>

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "3rem 1.5rem 5rem" }}>
        <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
          <div style={{
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem",
            letterSpacing: "0.18em", color: C.accent, textTransform: "uppercase",
            marginBottom: "0.7rem",
          }}>
            <Sparkles size={11} style={{ display: "inline", marginRight: 6 }} />
            Sblocca tutto il potere predittivo
          </div>
          <h1 style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
            fontSize: "clamp(2rem,5vw,3.4rem)", letterSpacing: "0.04em",
            textTransform: "uppercase", marginBottom: "0.7rem", lineHeight: 1.0,
          }}>
            Scegli il tuo <span style={{ color: C.accent }}>piano Premium</span>
          </h1>
          <p style={{
            color: C.muted, fontSize: "0.95rem", lineHeight: 1.6,
            maxWidth: 620, margin: "0 auto",
          }}>
            Modelli predittivi Monte Carlo, Brain Lab AI, Samurai Picker illimitato e molto altro.
            Trial gratuito di 3 giorni · cancelli quando vuoi.
          </p>
        </div>

        {isPremium && (
          <div data-testid="pricing-already-premium" style={{
            maxWidth: 560, margin: "0 auto 2rem",
            padding: "0.9rem 1.1rem",
            background: "rgba(34,255,163,0.06)",
            border: "1px solid rgba(34,255,163,0.35)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ShieldCheck size={18} style={{ color: "#22FFA3" }} />
              <div>
                <div style={{
                  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                  letterSpacing: "0.12em", textTransform: "uppercase",
                  fontSize: "0.85rem", color: "#22FFA3",
                }}>
                  Premium {premiumStatus === "lifetime" ? "a vita" : "attivo"}
                </div>
                <div style={{
                  fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem",
                  color: C.muted, marginTop: 2,
                }}>
                  {user?.email} · stato: {premiumStatus}
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate("/account")}
              data-testid="pricing-manage-btn"
              style={{
                background: "#22FFA3", color: "#020202",
                padding: "0.45rem 0.85rem", border: "none",
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
                letterSpacing: "0.14em", textTransform: "uppercase",
                fontSize: "0.78rem", cursor: "pointer",
              }}
            >
              Gestisci
            </button>
          </div>
        )}

        {error && (
          <div data-testid="pricing-error" style={{
            maxWidth: 560, margin: "0 auto 1.5rem",
            padding: "0.7rem 1rem", background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.4)", color: C.red,
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.74rem",
          }}>
            ⚠ {error}
          </div>
        )}

        {/* Plans grid */}
        <div style={{
          display: "grid", gap: 18,
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        }}>
          {loading
            ? [1, 2, 3].map((i) => (
              <div key={i} style={{
                height: 480, background: C.surface,
                border: `1px solid ${C.border}`, opacity: 0.5,
              }} />
            ))
            : PLAN_ORDER
                .map((k) => plans.find((p) => p.key === k))
                .filter(Boolean)
                .map((p) => {
              const meta = PLAN_META[p.key] || PLAN_META.monthly;
              const Icon = meta.icon;
              const featured = p.key === "monthly";
              return (
                <div key={p.key} data-testid={`pricing-card-${p.key}`} style={{
                  position: "relative",
                  background: featured ? meta.bg : C.surface,
                  border: `1px solid ${featured ? meta.border : C.border}`,
                  padding: "1.6rem 1.4rem 1.8rem",
                  display: "flex", flexDirection: "column",
                  boxShadow: featured ? `0 0 32px ${meta.color}33` : "none",
                  transform: featured ? "translateY(-8px)" : "none",
                }}>
                  {(() => {
                    const badgeText = computeDiscountBadge(p.key, p.price_eur) || meta.badge;
                    return badgeText && (
                      <div data-testid={`pricing-badge-${p.key}`} style={{
                        position: "absolute", top: -10, right: 14,
                        background: meta.color, color: "#020202",
                        fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
                        fontSize: "0.65rem", letterSpacing: "0.16em",
                        padding: "0.18rem 0.55rem",
                        boxShadow: featured ? `0 0 14px ${meta.color}aa` : "none",
                      }}>
                        {badgeText}
                      </div>
                    );
                  })()}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "1rem" }}>
                    <Icon size={22} style={{ color: meta.color }} />
                    <div style={{
                      fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
                      fontSize: "1.4rem", letterSpacing: "0.10em",
                      textTransform: "uppercase",
                    }}>
                      {p.name}
                    </div>
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    {meta.originalPrice && p.price_eur != null && (
                      <div
                        data-testid={`pricing-card-${p.key}-original`}
                        style={{
                          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                          fontSize: "1.15rem",
                          color: C.muted,
                          textDecoration: "line-through",
                          textDecorationColor: "rgba(255,255,255,0.55)",
                          textDecorationThickness: "2px",
                          marginBottom: 2,
                          letterSpacing: "0.02em",
                        }}
                      >
                        €{meta.originalPrice.toFixed(2).replace(".", ",")}
                      </div>
                    )}
                    <span style={{
                      fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
                      fontSize: "3.2rem", color: meta.color, letterSpacing: "-0.02em",
                      textShadow: featured ? `0 0 18px ${meta.color}88` : "none",
                    }}>
                      €{(p.price_eur != null ? p.price_eur : "—")}
                    </span>
                    <span style={{ color: C.muted, fontSize: "0.8rem", marginLeft: 6 }}>
                      {p.interval === "month" ? "/mese" : p.interval === "year" ? "/anno" : ""}
                    </span>
                  </div>
                  <div style={{ color: C.muted, fontSize: "0.75rem", marginBottom: "1.2rem" }}>
                    {SUBLINE[p.key]?.(p)}
                  </div>

                  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 1.5rem", flex: 1 }}>
                    {PLAN_BENEFITS.map((b, i) => (
                      <li key={i} style={{
                        display: "flex", alignItems: "flex-start", gap: 8,
                        padding: "0.32rem 0", fontSize: "0.82rem", color: "#D4D4D8",
                      }}>
                        <Check size={13} style={{ color: meta.color, marginTop: 3, flexShrink: 0 }} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleSubscribe(p.key)}
                    disabled={isPremium || busyPlan === p.key}
                    data-testid={`subscribe-${p.key}-btn`}
                    style={{
                      width: "100%", padding: "0.85rem",
                      background: isPremium ? "transparent" : meta.color,
                      color: isPremium ? C.muted : "#020202",
                      border: isPremium ? `1px solid ${C.border}` : "none",
                      fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
                      letterSpacing: "0.14em", textTransform: "uppercase",
                      fontSize: "0.95rem", cursor: isPremium ? "not-allowed" : "pointer",
                      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                      transition: "transform 0.15s, box-shadow 0.15s",
                      boxShadow: !isPremium && featured ? `0 0 18px ${meta.color}66` : "none",
                    }}
                    onMouseEnter={(e) => {
                      if (!isPremium && busyPlan !== p.key) {
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow = `0 0 24px ${meta.color}99`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = !isPremium && featured ? `0 0 18px ${meta.color}66` : "none";
                    }}
                  >
                    {busyPlan === p.key ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <ArrowRight size={14} />
                    )}
                    {isPremium
                      ? "Già attivo"
                      : (p.key === "lifetime" ? "Acquista a vita" : "Inizia trial gratuito")}
                  </button>
                </div>
              );
            })}
        </div>

        {/* ── Sportium affiliate: prominent placement (Premium gratis tramite deposito) ── */}
        {sportium && !isPremium && (
          <>
            <style>{`
              @keyframes bcSportiumPulse {
                0%, 100% { box-shadow: 0 0 24px rgba(239,68,68,0.32), 0 0 0 1px rgba(239,68,68,0.55); }
                50%      { box-shadow: 0 0 48px rgba(239,68,68,0.60), 0 0 0 1px rgba(239,68,68,0.90); }
              }
              @keyframes bcSportiumBadge {
                0%, 100% { transform: translateY(0) rotate(-2deg); }
                50%      { transform: translateY(-3px) rotate(-2deg); }
              }
              .bc-sportium-divider {
                display: flex; align-items: center; gap: 1.2rem;
                margin: 3rem auto 1.5rem; max-width: 620px;
                color: #EF4444; font-family: 'JetBrains Mono',monospace;
                font-size: 0.7rem; letter-spacing: 0.28em; text-transform: uppercase;
              }
              .bc-sportium-divider::before,
              .bc-sportium-divider::after {
                content: ""; flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(239,68,68,0.5), transparent);
              }
            `}</style>

            <div className="bc-sportium-divider" data-testid="sportium-divider">
              ★ Oppure paga ZERO con Sportium ★
            </div>

            <div data-testid="sportium-card" className="sportium-card" style={{
              position: "relative",
              background: "linear-gradient(135deg, rgba(239,68,68,0.14) 0%, rgba(30,15,15,0.98) 55%, rgba(20,20,24,0.98) 100%)",
              border: "1px solid rgba(239,68,68,0.60)",
              padding: "2.2rem 2rem 2rem",
              display: "grid",
              gridTemplateColumns: "minmax(200px, 260px) 1fr auto",
              gap: 28,
              alignItems: "center",
              animation: "bcSportiumPulse 2.6s ease-in-out infinite",
            }}>
              {/* Badge top-right — bigger + subtle bob */}
              <div style={{
                position: "absolute", top: -14, right: 22,
                background: "#EF4444", color: "#FFFFFF",
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
                fontSize: "0.85rem", letterSpacing: "0.18em",
                padding: "0.35rem 0.9rem",
                boxShadow: "0 0 18px rgba(239,68,68,0.85)",
                animation: "bcSportiumBadge 1.8s ease-in-out infinite",
                transformOrigin: "center",
              }}>
                🎁 {sportium.free_months || 2} MESI GRATIS
              </div>

              {/* Logo Sportium — bigger card */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "1.2rem",
                background: "#FFFFFF",
                borderRadius: 4,
                minHeight: 130,
                boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
              }}>
                <img
                  src="/sportium-logo.png"
                  alt="Sportium"
                  style={{ maxWidth: "100%", maxHeight: 100, objectFit: "contain" }}
                />
              </div>

              {/* Descrizione — enlarged headline */}
              <div>
                <div style={{
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.65rem", letterSpacing: "0.22em",
                  color: "#EF4444", textTransform: "uppercase",
                  marginBottom: 10, fontWeight: 700,
                }}>
                  Promo Esclusiva · Partner Ufficiale
                </div>
                <h3 style={{
                  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
                  fontSize: "clamp(1.7rem, 2.5vw, 2rem)", letterSpacing: "0.03em",
                  textTransform: "uppercase", lineHeight: 1.0,
                  marginBottom: 12, color: C.text,
                }}>
                  Registrati a Sportium e ottieni <span style={{ color: "#EF4444", textShadow: "0 0 12px rgba(239,68,68,0.6)" }}>{sportium.free_months || 2} mesi Premium GRATIS!</span>
                </h3>
                <p style={{ color: C.muted, fontSize: "0.9rem", lineHeight: 1.55, marginBottom: 6 }}>
                  <strong style={{ color: C.text }}>Come funziona (2 minuti):</strong>
                </p>
                <ol style={{ color: C.muted, fontSize: "0.85rem", lineHeight: 1.7, paddingLeft: 20, margin: 0 }}>
                  <li>Clicca <strong style={{ color: C.text }}>"Registrati gratis"</strong> qui a destra</li>
                  <li>Apri un conto Sportium e deposita <strong style={{ color: C.text }}>{sportium.deposit_amount_eur}€</strong></li>
                  <li>Invia lo screenshot del deposito a <strong style={{ color: "#EF4444" }}>{sportium.telegram_contact}</strong> su Telegram</li>
                  <li>Ricevi <strong style={{ color: C.accent }}>{sportium.free_months} mesi di Premium gratuiti</strong> ✨</li>
                </ol>
              </div>

              {/* CTA — bigger button */}
              <div style={{
                display: "flex", flexDirection: "column", gap: 10,
                alignItems: "stretch", minWidth: 220,
              }}>
                <a
                  href={sportium.registration_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sportium-register-btn"
                  style={{
                    padding: "1.1rem 1.5rem",
                    background: "#EF4444",
                    color: "#FFFFFF",
                    border: "none",
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontWeight: 900,
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    fontSize: "1.05rem",
                    cursor: "pointer",
                    textAlign: "center",
                    textDecoration: "none",
                    boxShadow: "0 0 26px rgba(239,68,68,0.55)",
                    transition: "transform 0.15s, box-shadow 0.15s",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 0 40px rgba(239,68,68,0.95)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 0 26px rgba(239,68,68,0.55)";
                  }}
                >
                  <ArrowRight size={17} /> Registrati gratis
                </a>
                <a
                  href={`https://t.me/${(sportium.telegram_contact || "").replace(/^@/, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sportium-telegram-link"
                  style={{
                    padding: "0.65rem 0.9rem",
                    background: "transparent",
                    color: C.text,
                    border: `1px solid ${C.border}`,
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: "0.72rem",
                    letterSpacing: "0.10em",
                    textAlign: "center",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                >
                  💬 Contatta {sportium.telegram_contact}
                </a>
              </div>
            </div>
          </>
        )}

        {/* ── Scroll hint: "Cosa ottieni con Premium?" — invita l'utente a esplorare la showcase sotto ── */}
        {!loading && !isPremium && (
          <>
            <style>{`
              @keyframes bcPremiumHintBob {
                0%, 100% { transform: translateY(0); }
                50%      { transform: translateY(8px); }
              }
              @keyframes bcPremiumHintGlow {
                0%, 100% {
                  box-shadow: 0 0 0 1px ${C.accent}55, 0 0 14px ${C.accent}44;
                }
                50% {
                  box-shadow: 0 0 0 1px ${C.accent}, 0 0 28px ${C.accent}99;
                }
              }
              @keyframes bcPremiumHintChevron {
                0%, 100% { transform: translateY(0); opacity: 0.85; }
                50%      { transform: translateY(4px); opacity: 1; }
              }
            `}</style>
            <div
              data-testid="premium-scroll-hint"
              role="button"
              tabIndex={0}
              onClick={() => {
                document.querySelector('[data-testid="premium-showcase"]')
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  document.querySelector('[data-testid="premium-showcase"]')
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }}
              style={{
                marginTop: "2.5rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.65rem",
                cursor: "pointer",
                userSelect: "none",
                padding: "0.85rem 1.5rem 1rem",
                width: "fit-content",
                marginInline: "auto",
                background: "rgba(200,255,0,0.04)",
                border: `1px solid ${C.accent}55`,
                animation: "bcPremiumHintBob 2.2s ease-in-out infinite, bcPremiumHintGlow 2.4s ease-in-out infinite",
              }}
            >
              <span style={{
                fontFamily: "'Barlow Condensed',sans-serif",
                fontWeight: 800,
                fontSize: "1rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: C.accent,
                textShadow: `0 0 10px ${C.accent}99`,
              }}>
                Cosa ottieni con Premium?
              </span>
              <ChevronDown
                size={26}
                style={{
                  color: C.accent,
                  filter: `drop-shadow(0 0 8px ${C.accent}cc)`,
                  animation: "bcPremiumHintChevron 1.2s ease-in-out infinite",
                }}
              />
            </div>
          </>
        )}


        {/* ── Showcase: cosa sblocchi davvero con Premium ── */}
        <section data-testid="premium-showcase" style={{ marginTop: "4rem" }}>
          <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
            <div style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.7rem", letterSpacing: "0.24em",
              color: C.accent, textTransform: "uppercase",
              marginBottom: "0.6rem",
            }}>
              ✦ Cosa Sblocchi con Premium ✦
            </div>
            <h2 style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontWeight: 900,
              fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
              letterSpacing: "0.04em",
              lineHeight: 1.05,
              textTransform: "uppercase",
              color: C.text,
              margin: 0,
            }}>
              Le funzioni che fanno <span style={{ color: C.accent }}>la differenza</span>
            </h2>
            <p style={{
              color: C.muted,
              fontSize: "0.92rem",
              maxWidth: 640,
              margin: "0.9rem auto 0",
              lineHeight: 1.6,
            }}>
              Bet Cracker non è una lista di pronostici. È un set di strumenti che
              ti fanno arrivare alla tua scommessa con un edge reale. Ecco cosa
              succede quando sblocchi Premium.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            {PREMIUM_FEATURES.map((f, idx) => {
              const reverse = idx % 2 === 1;
              const Icon = f.icon;
              return (
                <article
                  key={f.title}
                  data-testid={`feature-${idx}`}
                  style={{
                    position: "relative",
                    display: "grid",
                    gridTemplateColumns: "minmax(0,1fr)",
                    gap: 0,
                    padding: "1.6rem 1.5rem 1.7rem",
                    background: `linear-gradient(${reverse ? "270deg" : "90deg"}, ${f.accent}08 0%, ${C.surface} 65%)`,
                    border: `1px solid ${C.border}`,
                    borderLeft: reverse ? `1px solid ${C.border}` : `2px solid ${f.accent}`,
                    borderRight: reverse ? `2px solid ${f.accent}` : `1px solid ${C.border}`,
                    overflow: "hidden",
                  }}
                >
                  {/* glow accent in the corner */}
                  <div style={{
                    position: "absolute",
                    top: reverse ? "auto" : -40,
                    bottom: reverse ? -40 : "auto",
                    [reverse ? "right" : "left"]: -40,
                    width: 180, height: 180,
                    background: `radial-gradient(circle, ${f.accent}22 0%, transparent 70%)`,
                    pointerEvents: "none",
                  }} />

                  <div style={{
                    display: "flex",
                    flexDirection: reverse ? "row-reverse" : "row",
                    alignItems: "flex-start",
                    gap: "1.2rem",
                    position: "relative",
                    zIndex: 1,
                    flexWrap: "wrap",
                  }}>
                    {/* Icon */}
                    <div style={{
                      flexShrink: 0,
                      width: 56, height: 56,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: `${f.accent}12`,
                      border: `1px solid ${f.accent}44`,
                      borderRadius: 4,
                      boxShadow: `0 0 18px ${f.accent}22`,
                    }}>
                      <Icon size={24} style={{ color: f.accent, filter: `drop-shadow(0 0 6px ${f.accent}66)` }} />
                    </div>

                    {/* Text */}
                    <div style={{
                      flex: "1 1 320px",
                      textAlign: reverse ? "right" : "left",
                    }}>
                      <div style={{
                        fontFamily: "'JetBrains Mono',monospace",
                        fontSize: "0.62rem",
                        letterSpacing: "0.20em",
                        textTransform: "uppercase",
                        color: f.accent,
                        marginBottom: "0.4rem",
                      }}>
                        {f.eyebrow}
                      </div>
                      <h3 style={{
                        fontFamily: "'Barlow Condensed',sans-serif",
                        fontWeight: 900,
                        fontSize: "1.55rem",
                        letterSpacing: "0.03em",
                        textTransform: "uppercase",
                        color: C.text,
                        margin: "0 0 0.7rem",
                        lineHeight: 1.1,
                      }}>
                        {f.title}
                      </h3>
                      <p style={{
                        color: "#D4D4D8",
                        fontSize: "0.88rem",
                        lineHeight: 1.65,
                        margin: "0 0 0.9rem",
                      }}>
                        {f.body}
                      </p>
                      <ul style={{
                        listStyle: "none",
                        padding: 0,
                        margin: 0,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.4rem 0.6rem",
                        justifyContent: reverse ? "flex-end" : "flex-start",
                      }}>
                        {f.bullets.map((b) => (
                          <li key={b} style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "0.32rem 0.65rem",
                            background: `${f.accent}10`,
                            border: `1px solid ${f.accent}33`,
                            color: "#E4E4E7",
                            fontFamily: "'JetBrains Mono',monospace",
                            fontSize: "0.7rem",
                            letterSpacing: "0.02em",
                          }}>
                            <Check size={11} style={{ color: f.accent, flexShrink: 0 }} />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {/* closing line */}
          <div style={{
            marginTop: "1.8rem",
            padding: "1.2rem 1.4rem",
            textAlign: "center",
            background: `linear-gradient(90deg, transparent 0%, ${C.accent}08 50%, transparent 100%)`,
            border: `1px dashed ${C.accent}33`,
          }}>
            <Sparkles size={14} style={{ color: C.accent, marginBottom: 6 }} />
            <div style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontWeight: 800,
              fontSize: "1.1rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: C.text,
            }}>
              …e tantissime altre funzioni di analisi
            </div>
            <div style={{
              color: C.muted,
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.7rem",
              letterSpacing: "0.12em",
              marginTop: 4,
            }}>
              Monte Carlo · Match DNA · Distribuzione gol avanzata · Pronostici AI · Stats personali · e oltre.
            </div>
          </div>
        </section>

        {/* FAQ minimal */}
        <div style={{
          marginTop: "3rem", padding: "1.6rem 1.4rem",
          background: C.surface, border: `1px solid ${C.border}`,
        }}>
          <div style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
            letterSpacing: "0.16em", textTransform: "uppercase",
            fontSize: "0.95rem", marginBottom: "1rem", color: C.accent,
          }}>
            Domande frequenti
          </div>
          {[
            ["Posso cancellare in qualsiasi momento?", "Sì. Mensile e annuale puoi annullare con un click dal portale clienti. Mantieni Premium fino alla fine del periodo già pagato."],
            ["Cosa succede dopo i 3 giorni di trial?", "Allo scadere del trial scatta il primo pagamento automatico. Non vuoi continuare? Annulla prima della scadenza, nessun addebito."],
            ["Come funziona la promo Sportium?", `Registrati a Sportium via il nostro link partner, deposita ${sportium?.deposit_amount_eur || 30}€, invia lo screenshot a ${sportium?.telegram_contact || "@Varagasy"} su Telegram. Riceverai ${sportium?.free_months || 2} mesi di Premium gratuiti, senza addebito di carta.`],
            ["Hai un vecchio codice BCRK-XXXX?", "Crea il tuo account email e usa 'Collega codice' nella pagina Account: avrai Premium gratis a vita."],
            ["Metodi di pagamento accettati?", "Carte di credito/debito (Visa, Mastercard, AmEx), Apple Pay, Google Pay e PayPal tramite Stripe."],
          ].map(([q, a]) => (
            <div key={q} style={{ padding: "0.7rem 0", borderTop: `1px solid ${C.border}` }}>
              <div style={{ color: C.text, fontSize: "0.85rem", fontWeight: 600, marginBottom: 4 }}>{q}</div>
              <div style={{ color: C.muted, fontSize: "0.78rem", lineHeight: 1.55 }}>{a}</div>
            </div>
          ))}
        </div>
      </main>

      {/* Telegram username capture modal (shown before each checkout) */}
      {tgModalPlan && (
        <div
          data-testid="telegram-modal-overlay"
          onClick={() => !busyPlan && setTgModalPlan(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 999,
            background: "rgba(0,0,0,0.78)",
            backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "1.5rem",
          }}
        >
          <div
            data-testid="telegram-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.surface,
              border: `1px solid ${C.accent}55`,
              padding: "1.75rem 1.5rem",
              maxWidth: 460, width: "100%",
              boxShadow: `0 0 40px ${C.accent}22`,
            }}
          >
            <div style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
              fontSize: "1.4rem", letterSpacing: "0.14em", textTransform: "uppercase",
              color: C.text, marginBottom: "0.4rem",
            }}>
              Un ultimo passo
            </div>
            <div style={{
              color: C.muted, fontSize: "0.85rem", lineHeight: 1.55,
              marginBottom: "1.1rem",
            }}>
              Inserisci il tuo username <strong style={{ color: C.cyan }}>Telegram</strong> —
              ci serve per <strong style={{ color: C.accent }}>aggiungerti al gruppo VIP</strong> e
              per assisterti rapidamente in caso di problemi con l&apos;abbonamento.
            </div>
            <label style={{
              display: "block",
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.62rem", color: C.muted,
              letterSpacing: "0.14em", textTransform: "uppercase",
              marginBottom: "0.35rem",
            }}>
              Username Telegram
            </label>
            <input
              type="text"
              data-testid="telegram-modal-input"
              value={tgInput}
              onChange={(e) => { setTgInput(e.target.value); setTgError(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") confirmTelegramAndCheckout(); }}
              placeholder="@mario_rossi"
              autoFocus
              disabled={!!busyPlan}
              style={{
                width: "100%",
                background: C.surfaceHi,
                border: `1px solid ${tgError ? C.red : C.border}`,
                color: C.text,
                padding: "0.75rem 0.9rem",
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.95rem",
                outline: "none",
                marginBottom: "0.6rem",
              }}
            />
            {tgError && (
              <div
                data-testid="telegram-modal-error"
                style={{
                  color: C.red,
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.72rem",
                  marginBottom: "0.6rem",
                }}
              >
                {tgError}
              </div>
            )}
            <div style={{
              color: C.muted, fontSize: "0.7rem", marginBottom: "1.2rem",
              fontFamily: "'JetBrains Mono',monospace",
            }}>
              Formato: deve iniziare con <code style={{ color: C.cyan }}>@</code> · 5-32 caratteri
            </div>
            <div style={{ display: "flex", gap: "0.6rem", justifyContent: "flex-end" }}>
              <button
                onClick={() => setTgModalPlan(null)}
                disabled={!!busyPlan}
                data-testid="telegram-modal-cancel"
                style={{
                  background: "transparent",
                  border: `1px solid ${C.border}`,
                  color: C.muted,
                  padding: "0.6rem 1rem",
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 700, fontSize: "0.85rem",
                  letterSpacing: "0.12em", textTransform: "uppercase",
                  cursor: busyPlan ? "wait" : "pointer",
                }}
              >
                Annulla
              </button>
              <button
                onClick={confirmTelegramAndCheckout}
                disabled={!!busyPlan}
                data-testid="telegram-modal-confirm"
                style={{
                  background: busyPlan ? C.surfaceHi : C.accent,
                  border: `1px solid ${C.accent}`,
                  color: busyPlan ? C.muted : "#000",
                  padding: "0.6rem 1.2rem",
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 800, fontSize: "0.9rem",
                  letterSpacing: "0.12em", textTransform: "uppercase",
                  cursor: busyPlan ? "wait" : "pointer",
                  display: "inline-flex", alignItems: "center", gap: "0.4rem",
                  boxShadow: busyPlan ? "none" : `0 0 14px ${C.accent}55`,
                }}
              >
                {busyPlan ? <Loader2 size={13} className="animate-spin" /> : <ArrowRight size={13} />}
                {busyPlan ? "Vai al checkout…" : "Vai al pagamento"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
